// src/components/ExamAnswerPanel.jsx
// Word Processor / Spreadsheet editor, rendered inside an ExamFloatingPanel
// instead of a separate popup window. Same debounce + 30s max-wait autosave
// heartbeat + write guard as before - only where it runs has changed. No
// separate Firestore subscription needed here: ExamWindowApp already keeps
// `answers` live via its own onSnapshot, so this just reads `initialValue`
// once at mount (like the old popup did on load) and writes autosaves back
// - the next snapshot tick naturally reflects them everywhere else.

import React, { useRef, useCallback, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import ExamRichTextEditor from './ExamRichTextEditor';
import ExamSpreadsheetGrid from './ExamSpreadsheetGrid';
import { useWriteGuard } from '../utils/writeGuard';

const AUTOSAVE_DEBOUNCE_MS = 30000;
const AUTOSAVE_MAX_WAIT_MS = 30000;

function plainTextWordCount(html) {
  const text = (html || '').replace(/<[^>]*>/g, ' ').trim();
  return text ? text.split(/\s+/).length : 0;
}

const ExamAnswerPanel = ({ sessionId, sectionId, mode, initialValue, initialSavedAt, sectionName }) => {
  // Seeded from the last save already sitting in Firestore (see
  // ExamSplitScreen), not just null - otherwise closing and reopening this
  // panel would forget the content was already saved and show "Not yet
  // saved" again until the next autosave, even though nothing has changed.
  const [savedAt, setSavedAt] = React.useState(() => initialSavedAt?.toDate ? initialSavedAt.toDate() : null);
  const debounceTimerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);
  const latestValueRef = useRef(initialValue || '');
  const { guard, blocked: writeBlocked } = useWriteGuard();

  const flush = useCallback(() => {
    if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
    if (maxWaitTimerRef.current) { clearTimeout(maxWaitTimerRef.current); maxWaitTimerRef.current = null; }
    if (!sessionId || !sectionId) return;
    const value = latestValueRef.current;

    guard(async () => {
      try {
        // 'spreadsheet'/'wordprocessor' are the (possibly prepopulated)
        // primary panels; 'blankspreadsheet'/'blankwordprocessor' the
        // optional second always-starts-blank ones (see functions/index.js's
        // initialAnswers seeding) - each saves to its own answers.{sectionId}
        // subfield, same shape as its primary counterpart.
        let field;
        if (mode === 'spreadsheet' || mode === 'blankspreadsheet') {
          const subfield = mode === 'spreadsheet' ? 'spreadsheet' : 'blankSpreadsheet';
          field = { [`answers.${sectionId}.${subfield}`]: { gridJson: value, updatedAt: serverTimestamp() } };
        } else {
          const subfield = mode === 'blankwordprocessor' ? 'blankWordProcessor' : 'wordProcessor';
          field = {
            [`answers.${sectionId}.${subfield}`]: {
              html: value,
              wordCount: plainTextWordCount(value),
              updatedAt: serverTimestamp()
            }
          };
        }
        // Also refreshes the session-level idle clock sweepAbandonedExamSessions
        // uses for unlimited-time sessions (functions/index.js) - without this,
        // an untimed exam a student is actively working on would still get
        // swept as if abandoned, since that check would otherwise only ever
        // see the session's original start time.
        field.lastAutosaveAt = serverTimestamp();
        await updateDoc(doc(db, 'examSessions', sessionId), field);
        setSavedAt(new Date());
      } catch (err) {
        console.error('Autosave failed:', err);
      }
    });
  }, [sessionId, sectionId, mode, guard]);

  const handleChange = (value) => {
    latestValueRef.current = value;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS);

    if (!maxWaitTimerRef.current) {
      maxWaitTimerRef.current = setTimeout(flush, AUTOSAVE_MAX_WAIT_MS);
    }
  };

  // Best-effort save if the panel is closed (unmounted) before either timer
  // fires - see ExamAnswerPanel's timers above for what actually bounds
  // potential data loss (this is just a bonus attempt, not the guarantee).
  useEffect(() => () => {
    if (debounceTimerRef.current || maxWaitTimerRef.current) flush();
  }, [flush]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '6px 12px', fontSize: '11px', color: '#95a5a6', borderBottom: '1px solid #e2e8f0', textAlign: 'right', flexShrink: 0 }}>
        {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Not yet saved'}
      </div>

      {writeBlocked && (
        <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', fontSize: '12px', fontWeight: '600', flexShrink: 0 }}>
          Something went wrong and autosave has stopped here. Close and reopen this panel and check your work is still there.
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, padding: '12px', overflow: 'auto' }}>
        {(mode === 'spreadsheet' || mode === 'blankspreadsheet') ? (
          <ExamSpreadsheetGrid value={initialValue} onChange={handleChange} />
        ) : (
          <ExamRichTextEditor value={initialValue} onChange={handleChange} placeholder={`Type your answer for ${sectionName} here...`} />
        )}
      </div>
    </div>
  );
};

export default ExamAnswerPanel;
